import axios from 'axios';
import { jwtDecrypt } from '../../shared/helper';
import { Preferences } from '@capacitor/preferences';

const state = () => ({
  authData: {
    token: '',
    tokenExp: '',
    userId: '',
    userEmail: '',
  },
  loggedIn: false,
  profile: {
    role: null,
    assignment: null,
    groups: [],
  },
});

const getters = {
  getLoginStatus(state) {
    return state.loggedIn;
  },
  getAuthData(state) {
    return state.authData;
  },
  getProfile(state) {
    return state.profile;
  },
};

const mutations = {
  saveAuthToken(state, payload) {
    const jwtDecodeUserInfo = jwtDecrypt(payload.accessToken);
    const newAuthData = {
      token: payload.accessToken,
      tokenExp: jwtDecodeUserInfo.exp,
      userId: jwtDecodeUserInfo.sub,
      userEmail: jwtDecodeUserInfo.email,
    };
    state.authData = newAuthData;
  },
  clearAuthToken(state) {
    state.authData = {
      token: '',
      tokenExp: '',
      userId: '',
      userEmail: '',
    };
    state.loggedIn = false;
  },
  saveLoginStatus(state, status) {
    state.loggedIn = status;
  },
  setProfile(state, payload) {
    const { role, assignment, groups } = payload;

    state.profile.role = {
      id: role.id,
      name: role.name,
      modules: Array.isArray(role.modules)
        ? role.modules.reduce((acc, roleModule) => {
            const { identifier } = roleModule.module;
            const permissions = [];
            if (roleModule.create) permissions.push('CREATE');
            if (roleModule.read) permissions.push('READ');
            if (roleModule.update) permissions.push('UPDATE');
            if (roleModule.delete) permissions.push('DELETE');
            if (roleModule.import) permissions.push('IMPORT');
            if (roleModule.export) permissions.push('EXPORT');

            acc[identifier] = permissions;
            return acc;
          }, {})
        : role.modules,
    };
    state.profile.assignment = assignment
      ? { id: assignment.id, name: assignment.name }
      : null;
    state.profile.groups = groups.map((g) => {
      return {
        id: g.id,
        name: g.name,
        isPrimary: g.isPrimary || false,
        defaultDivision: g.defaultDivision
          ? { id: g.defaultDivision.id, name: g.defaultDivision.name }
          : null,
      };
    });
  },
  clearProfile(state) {
    state.profile.role = null;
    state.profile.assignment = null;
    state.profile.groups = [];
  },
};

const actions = {
  async login({ commit, dispatch }, payload) {
    try {
      const response = (
        await axios.post(`${process.env.VUE_APP_API_URL}/auth/login`, payload)
      ).data;

      await Preferences.set({
        key: 'accessToken',
        value: response.accessToken,
      });

      commit('saveAuthToken', response);
      commit('saveLoginStatus', true);

      try {
        const profile = (
          await axios.get(`${process.env.VUE_APP_API_URL}/auth/profile`, {
            headers: {
              Authorization: 'Bearer ' + response.accessToken,
            },
          })
        ).data.user;

        const hasAppAccess = profile.role.modules.find(
          (m) => m.module.identifier === 'APP' && m.read
        );

        if (!hasAppAccess) {
          await dispatch('logout');
          return;
        }

        await dispatch('saveProfile', profile);
      } catch (e) {
        dispatch('removeProfile');
      }
    } catch (e) {
      commit('saveLoginStatus', false);
    }
  },
  async logout({ commit, dispatch }) {
    await Preferences.remove({ key: 'accessToken' });
    await Preferences.remove({ key: 'profile' });

    commit('clearAuthToken');
    commit('clearProfile');

    dispatch('offline/clearState', null, { root: true });
    dispatch('offline/deleteAllOfflineSavedItems', null, { root: true });
  },
  async loadPreferencesTokens({ commit }) {
    const accessToken = await Preferences.get({ key: 'accessToken' });

    if (accessToken.value) {
      const tokenData = {
        accessToken: accessToken.value,
      };
      commit('saveAuthToken', tokenData);
    }
  },
  async loadProfile({ state, dispatch }) {
    if (!state.authData.token) return;

    try {
      const profile = (
        await axios.get(`${process.env.VUE_APP_API_URL}/auth/profile`, {
          headers: {
            Authorization: 'Bearer ' + state.authData.token,
          },
        })
      ).data.user;

      await dispatch('saveProfile', profile);
    } catch (e) {
      dispatch('removeProfile');
    }
  },
  async saveProfile({ state, commit, dispatch }, payload) {
    commit('setProfile', payload);

    await Preferences.set({
      key: 'profile',
      value: JSON.stringify(state.profile),
    });

    // The profile payload also carries the user preferences and language: the
    // server state is applied here, for both the login and the startup paths.
    await dispatch('prefs/hydrateFromProfile', payload, { root: true });
  },
  async removeProfile({ commit }) {
    commit('clearProfile');
    await Preferences.remove({ key: 'profile' });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
